import {
  faGlobe,
  faLocationDot,
  faMagnifyingGlass,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import debounce from "lodash.debounce";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCaMap } from "../state";
import { GPSPlugin, PDOKPlugin, Suggest } from "./plugins";

const Icon = ({ global = false }) => {
  return (
    <FontAwesomeIcon
      icon={global ? faGlobe : faLocationDot}
      className="w-2.5 h-2.5 fill-dark-green group-hover:fill-hover"
    />
  );
};

const SearchBox = ({ setInput, className, setShowSuggests, onClear = undefined }) => {
  const handle = useMemo(
    () =>
      debounce((e) => {
        setInput(e.target.value);
        setShowSuggests(true);
      }, 250),
    [setInput, setShowSuggests]
  );
  const focus = useCallback((e) => setShowSuggests(e?.type === "focus"), [setShowSuggests]);
  const input = useRef(null);
  return (
    <div className={className || "flex items-center justify-end bg-white rounded-sm"}>
      <input
        type="text"
        name="search"
        className="h-12 w-full text-sm p-4 outline-none bg-transparent border-none focus:border-none"
        placeholder="Zoek op adres, perceel of GPS"
        onChange={handle}
        onBlur={() => setTimeout(focus, 200)}
        onFocus={focus}
        ref={input}
      />

      <div className="flex shrink h-8 divide-x divide-gray">
        <button className="h-full flex items-center px-4 text-dark-gray hover:text-yellow/90">
          <FontAwesomeIcon icon={faMagnifyingGlass} className="w-4 h-4" />
        </button>
        <button
          className="h-full flex items-center px-4 text-dark-gray hover:text-yellow/90"
          onClick={(e) => {
            handle(e);
            onClear?.(input.current?.value);
            if (input.current) input.current.value = "";
          }}
        >
          <FontAwesomeIcon icon={faXmark} className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
};

interface SearchResultsProps {
  results: Suggest[];
  select?: (s: Suggest) => void;
  className?: string;
}
const SearchResults = ({ results, select, className }: SearchResultsProps) => {
  const c = className || "bg-white rounded-sm max-h-100 w-full mt-[1px]";
  return (
    results?.length && (
      <div className={`flex items-center ${c}`}>
        <ul className="p-4 font-roboto text-sm text-dark">
          {results.map((r, index) => (
            <li className="p-1" key={index}>
              <button className="flex items-baseline group" onClick={() => select(r)}>
                <Icon global={r.type === "zone"} />
                <span className="pl-2.5 group-hover:text-yellow/90">{r.description}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    )
  );
};

const useSuggestTo = (plugins) => {
  const map = useCaMap();
  const navigate = useNavigate();
  return useCallback(
    async (s: Suggest) => {
      for (const p of plugins) {
        const l = await p.geocode(s.placeId);
        if (!l?.location) continue;
        if (l.exact) {
          const { lat, lng } = l.location;
          navigate(`/location/${lng.toFixed(5)},${lat.toFixed(5)}`);
          return;
        }
        map.flyTo({
          center: l.location,
        });
        // TODO set geometry in selection layer
        return;
      }
    },
    [map, plugins, navigate]
  );
};

const updateFirstSuggestions = async (input, plugins, setSuggests) => {
  if (input?.length <= 2) return;
  for (const p of plugins) {
    const n = await p.suggests(input, {});
    if (n) {
      setSuggests(n);
      break;
    }
  }
};

const emptyArray = [];
Object.freeze(emptyArray);

export interface SearchProps {
  className?: string;
  resultClassName?: string;
  inputClassName?: string;
  onClear?: (e) => void;
}
const plugins = [new GPSPlugin(), new PDOKPlugin()];

export const Search = ({ className, resultClassName, inputClassName, ...props }: SearchProps) => {
  const [input, setInput] = useState<string | undefined>();
  const [suggests, setSuggests] = useState<Suggest[]>(emptyArray);
  const [showSuggests, setShowSuggests] = useState(false);
  const select = useSuggestTo(plugins);
  // todo use geocode
  const shouldClearSuggests = input?.length < 2 && suggests.length > 0;
  useEffect(() => {
    if (input) updateFirstSuggestions(input, plugins, setSuggests);
  }, [input, setSuggests]);
  useEffect(() => {
    if (shouldClearSuggests) setSuggests(emptyArray);
  }, [shouldClearSuggests, suggests, setSuggests]);

  return (
    <div className={className || ""}>
      <SearchBox
        setInput={setInput}
        className={inputClassName}
        setShowSuggests={setShowSuggests}
        {...props}
      />
      {!(showSuggests && suggests.length) ? null : (
        <SearchResults className={resultClassName} results={suggests} select={select} />
      )}
    </div>
  );
};
